import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FaChartLine, FaFileExcel } from 'react-icons/fa';
import style from './ContractSummaryReport.module.css';

const FIXED_PARAMS = {
  status: 'paid',
  dateField: 'paymentDate'
};

const ContractSummaryReport = () => {
  const [reportData, setReportData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchReport = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get('/rentals/admin/reports/payments-excel', {
        params: FIXED_PARAMS
      });
      const responseBody = response.data || {};
      const payload = Array.isArray(responseBody?.columns) && Array.isArray(responseBody?.data)
        ? responseBody
        : (responseBody?.data || {});

      setColumns(payload?.columns || []);
      setReportData(payload?.data || []);
      setCount(payload?.count ?? (payload?.data || []).length);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
      setColumns([]);
      setReportData([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatValue = (key, value) => {
    if (value == null || value === '') return '-';
    const currencyKeys = [
      'valorCuota',
      'importeAdministracion',
      'gananciaAgente',
      'gananciaOficina',
      'liquidacionPropietario'
    ];
    if (currencyKeys.includes(key)) {
      return new Intl.NumberFormat('es-AR', {
        style: 'currency',
        currency: 'ARS',
        minimumFractionDigits: 0
      }).format(value || 0);
    }
    if (key === 'porcentajeAdministracion') {
      return `${value}%`;
    }
    if (key.toLowerCase().includes('fecha')) {
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? '-' : parsed.toLocaleDateString('es-AR');
    }
    return value;
  };

  const getColumnLabel = (column) => {
    const columnLabels = {
      'porcentajeAdministracion': '%'
    };
    return columnLabels[column] || column;
  };

  return (
    <div className={style.container}>
      <div className={style.header}>
        <h1 className={style.title}>
          <FaChartLine className={style.titleIcon} />
          Reporte de Cuotas
        </h1>
        <p className={style.subtitle}>
          Detalle completo de todas las cuotas con cálculo de administración.
        </p>
      </div>

      {loading && <div className={style.loading}>Cargando reporte...</div>}
      {error && <div className={style.error}>Error: {error}</div>}

      {!loading && !error && reportData.length === 0 && (
        <div className={style.noData}>
          No hay datos disponibles.
        </div>
      )}

      {!loading && !error && reportData.length > 0 && (
        <div className={style.tableWrapper}>
          {(() => {
            const visibleColumns = columns.filter((column) => {
              if (column !== 'agente' && column !== 'oficina') return true;
              return reportData.some((row) => row[column] != null && row[column] !== '');
            });

            return (
              <>
          <div className={style.tableHeader}>
            <div>
              <FaFileExcel /> Total de registros: {count}
            </div>
            <div className={style.filtersSummary}>
              Filtros fijos: status=paid · dateField=paymentDate
            </div>
          </div>
          <table className={style.table}>
            <thead>
              <tr>
                {visibleColumns.map(column => (
                  <th key={column} title={column}>{getColumnLabel(column)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {reportData.map((row, index) => (
                <tr key={row.paymentId || index}>
                  {visibleColumns.map(column => (
                    <td 
                      key={`${column}-${index}`}
                      title={column}
                    >
                      {formatValue(column, row[column])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default ContractSummaryReport;
